import type { DetectedCategory } from '@pages/Menu/Products/MenuUploaderV2/types/menuUpload.types';

/**
 * Categorías sintéticas creadas client-side desde el EditSheet
 * ("+ Crear categoría nueva…", spec UX §2.4). No vienen del análisis del
 * backend: nacen en el review y se persisten igual que las detectadas (el POST
 * de `handlePersist` no distingue origen).
 */

/**
 * Convierte un nombre libre en un slug estable para el id sintético:
 * minúsculas, sin acentos, espacios y símbolos colapsados a `_`.
 *
 * @param {string} name Nombre de la categoría tecleado por el usuario.
 * @returns {string} Slug seguro para usar en un id (puede ser vacío si el nombre no tiene alfanuméricos).
 */
export const slugifyCategoryName = (name: string): string =>
  name
    .trim()
    .toLowerCase()
    .normalize('NFD')
    // Quita marcas diacríticas (á → a) tras la descomposición NFD.
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

/**
 * Crea una `DetectedCategory` sintética con id `synthetic_<slug>`, evitando
 * colisiones con ids existentes mediante sufijo incremental (`_2`, `_3`, …).
 *
 * @param {string} name Nombre visible de la categoría nueva.
 * @param {Set<string>} existingIds Ids de categorías ya presentes en el análisis.
 * @returns {DetectedCategory | null} La categoría lista para inyectar al análisis, o null si el nombre queda vacío.
 */
export const buildSyntheticCategory = (name: string, existingIds: Set<string>): DetectedCategory | null => {
  const trimmed = name.trim();
  if (trimmed.length === 0) return null;
  const slug = slugifyCategoryName(trimmed) || 'categoria';
  let id = `synthetic_${slug}`;
  let suffix = 2;
  while (existingIds.has(id)) {
    id = `synthetic_${slug}_${suffix}`;
    suffix += 1;
  }
  return {
    id,
    type: 'category',
    name: trimmed,
    confidence: 1,
    originalText: trimmed,
  };
};
